const DEFAULT_FREE_TRIAL_MAX_EMPLOYEES = Number(process.env.REACT_APP_FREE_TRIAL_MAX_EMPLOYEES) || 2;

// Tier-based employee limits (must match backend TIER_CONFIG)
const TIER_EMPLOYEE_LIMITS = {
    trial: 2,
    starter: 50,
    pro: 150,
    enterprise: 500
};

const toLowerString = (value) => (value || '').toString().trim().toLowerCase();
const getNested = (obj, path) => path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);

export const extractSubscriptionPlan = (company) => {
    if (!company) return '';

    return toLowerString(
        company.subscription_plan ||
        company.subscriptionPlan ||
        company.plan ||
        company.plan_name ||
        company.tier ||
        getNested(company, 'subscription.plan') ||
        getNested(company, 'subscription.subscription_plan')
    );
};

export const extractTier = (company) => {
    if (!company) return 'trial';
    const plan = extractSubscriptionPlan(company);
    if (['starter', 'pro', 'enterprise'].includes(plan)) return plan;
    if (plan === 'trial') return 'trial';
    // Legacy plans map to tiers
    if (['monthly', 'quarterly', 'yearly'].includes(plan)) return 'pro';
    return 'trial';
};

export const hasSubscriptionPlan = (company) => Boolean(extractSubscriptionPlan(company));

export const getCompanyFromStorage = () => {
    try {
        const raw = localStorage.getItem('company');
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        console.error('Error reading company from localStorage:', error);
        return null;
    }
};

export const isFreeTrialCompany = (companyInput = null) => {
    const company = companyInput || getCompanyFromStorage();
    if (!company) return false;
    const tier = extractTier(company);
    return tier === 'trial';
};

export const getTierEmployeeLimit = (tier) => {
    return TIER_EMPLOYEE_LIMITS[tier] || TIER_EMPLOYEE_LIMITS.trial;
};

export const getFreeTrialLimits = () => ({
    maxEmployees: DEFAULT_FREE_TRIAL_MAX_EMPLOYEES
});

export const evaluateEmployeeLimit = ({ company = null, currentEmployeesCount = 0, addingCount = 1 }) => {
    const tier = extractTier(company);
    // Use the actual employee_limit from subscription if available, otherwise fall back to tier defaults
    const maxEmployees = company?.employee_limit || getTierEmployeeLimit(tier);
    const isTrial = tier === 'trial';

    const nextTotal = currentEmployeesCount + addingCount;
    const remaining = Math.max(0, maxEmployees - currentEmployeesCount);
    const allowed = nextTotal <= maxEmployees;

    return {
        allowed,
        isTrial,
        tier,
        limit: maxEmployees,
        nextTotal,
        remaining,
        message: allowed
            ? ''
            : isTrial
                ? `Free trial allows up to ${maxEmployees} employees. Upgrade your plan to add more.`
                : `Your ${tier} plan allows up to ${maxEmployees} employees. Upgrade to add more.`
    };
};
